import { Box, Container, Link, Typography } from "@material-ui/core"
import { visuallyHidden } from "@mui/utils"
import {
  postGiftCardGenerateVerificationCode,
  postGiftCardVerifyMobilePhone,
  setApiToken,
} from "api"
import { formatPhone } from "common/utils/formatters"
import GenericButton from "components/GenericButton/GenericButton"
import { useQuickPurchaseContext } from "components/QuickPurchasePage/QuickPurchaseContext"
import NextLink from "components/Reusable/Bit/NextLink"
import { useRouter } from "next/router"
import { createRef, useEffect, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import styled from "styled-components"
import BackIcon from "../Icons/BackIcon"
import { useOtpContext } from "./OtpContext"
import StyledCodeInput from "./StyledCodeInput"
import Timer from "./Timer"

const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

const StyledWrapper = styled(Container)`
  margin-top: 80px;
  position: relative;
  padding: 0px;
  padding-bottom: 70px;
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 0px;
    padding-right: 60px;
    padding-left: 60px;
    display: flex;
    flex-grow: 1;
    justify-content: center;
    align-items: center;
  }
`

const StyledTitle = styled(Typography).attrs({ variant: "h2" })`
  font-size: 40px;
  color: #000;
  line-height: 120%;
  letter-spacing: -0.02em;
  font-family: Speedee, NarkisBlock;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: 64px;
    line-height: 0.8;
    margin-bottom: 4px;
    padding: 0 16px;
  }
`
const StyledText = styled(Typography).attrs({ variant: "body1" })<{
  $fontWeight: string
  $marginTop: string
}>`
  line-height: 1.1;
  font-weight: ${(p) => p.$fontWeight};
  margin-top: ${(p) => p.$marginTop};
  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 24px;
    span {
      font-size: 20px;
    }
  }
`

const StyledLink = styled(Box)`
  a {
    display: flex;
  }
  p {
    margin-right: 8px;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    position: absolute;
    top: 120px;
    right: 60px;
  }
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-right: 16px;
    padding-top: 16px;
  }
  :hover {
    text-decoration: underline;
  }
`
const StyledActionsWrap = styled.div`
  margin-top: 75px;
  display: flex;
  justify-content: center;
  gap: 20px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: 23px;
  }
`
const StyledButtonLink = styled(Link).attrs({
  variant: "body1",
})<{ $disabled: boolean; component: string }>`
  color: ${(p) => (p.$disabled ? "#264f36" : "#757575;")};
  font-size: 24px;
  pointer-events: ${(p) => (p.$disabled ? "auto" : "none")};
  font-family: "Speedee", "NarkisBlock";
  font-weight: bold;
  text-decoration: none;

  :hover {
    text-decoration: underline;
  }

  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 24px;
  }
`

const StyledButtonWrapper = styled(Box)`
  width: 100%;
  height: 50px;
  margin-top: 70px;
  display: flex;
  justify-content: center;

  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 100%;
    position: fixed;
    right: 0;
    border-radius: 10px;
    box-shadow: 0 -5px 10px 0 hsla(0, 0%, 0%, 0.12);
    background-color: #fff;
    padding: 12px 16px 10px 16px;
    height: 70px;
    bottom: 0;
    margin-top: 0px;
    z-index: 10;
  }
`

const StyledError = styled(Typography).attrs({ variant: "body1" })`
  color: #d70202;
  line-height: 1.1;
  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 90%;
    margin: 0 auto;
  }
`

const StyledTextWrapper = styled(Box)`
  ${(p) => p.theme.breakpoints.up("md")} {
    display: flex;
    justify-content: center;
    :nth-child(2) {
      P {
        margin-right: 5px;
      }
    }
  }
`

const StyledBox = styled(Box)`
  margin-top: 120px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: 59px;
  }
`

const StyledSMSText = styled.span`
  font-family: Speedee;
  font-size: 20px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    font-size: 16px;
  }
`

type OtpType = {
  type: "balance" | "purchase" | "groupPurchases"
  submit: (success: boolean) => void
  editLink?: string
  prevPathLink?: string
}

const OtpPage = ({ type, submit, editLink, prevPathLink }: OtpType) => {
  const {
    totalTimeoutInSeconds,
    setTotalTimeoutInSeconds,
    phoneNumber,
    setAccessToken,
  } = useOtpContext()
  const { prevPath } = useQuickPurchaseContext()
  const router = useRouter()
  const [[mins, secs], setTime] = useState([0, 0])
  const [submitCode, setSubmitCode] = useState(null)
  const [disabled, setDisabled] = useState(true)
  const [submitError, setSubmitError] = useState("")

  const reCaptchaRef = createRef<ReCAPTCHA>()

  useEffect(() => {
    const minutes = Math.floor(totalTimeoutInSeconds / 60)
    const seconds = totalTimeoutInSeconds - minutes * 60
    setTime([minutes, parseInt(seconds.toFixed(0))])
  }, [totalTimeoutInSeconds])

  const hiddenText = () => {
    if ((mins + 1) * 60 === 120)
      return (
        <Box sx={visuallyHidden} role="alert" aria-live="polite">
          נותרו 2 דקות עד שניתן יהיה לשלוח קוד מחדש
        </Box>
      )
    else if ((mins + 1) * 60 === 60 && secs > 30)
      return (
        <Box sx={visuallyHidden} role="alert" aria-live="polite">
          נותרו 1 דקה עד שניתן יהיה לשלוח קוד מחדש
        </Box>
      )
    else if (mins === 0 && secs < 30 && secs > 0)
      return (
        <Box sx={visuallyHidden} role="alert" aria-live="polite">
          נותרו 0.5 דקה עד שניתן יהיה לשלוח קוד מחדש
        </Box>
      )
    else if (mins === 0 && secs === 0)
      return (
        <Box sx={visuallyHidden} role="alert" aria-live="polite">
          ניתן לשלוח קוד מחדש
        </Box>
      )
  }

  const getVerificationCodeAgain = async () => {
    reCaptchaRef?.current?.reset()
    const gRecaptchaResponse = await reCaptchaRef?.current?.executeAsync()
    setTotalTimeoutInSeconds(0)
    const response = await postGiftCardGenerateVerificationCode({
      mobilePhone: phoneNumber,
      toSendSms: true,
      gRecaptchaResponse,
    })
    if (response?.status === 200 && response?.data) {
      const totalTime = response.data.body.totalTimeoutInSeconds
      setTotalTimeoutInSeconds(totalTime)
    }
  }

  const handleSubmit = async (code?: string) => {
    const otpCode = submitCode || code

    try {
      setDisabled(true)
      const response = await postGiftCardVerifyMobilePhone({
        verificationCode: otpCode,
        mobilePhone: phoneNumber,
      })

      if (response?.status === 200 && response?.data && response?.data?.body) {
        setAccessToken(response.data.body.accessToken)
        setApiToken(response.data.body.accessToken)
        submit(true)
      } else if (response?.data && response?.data.errorCode === 429) {
        setSubmitError("בוצעו יותר מדי נסיונות כושלים, נסו שוב עוד דקה")
      } else if (response?.data && response?.data.errorCode === 2000) {
        submit(false)
      } else {
        setSubmitError("הקוד אינו תקין, בבקשה לנסות שוב")
      }

      // submit(
      //   !!(response?.status === 200 && response?.data && response?.data?.body)
      // )
    } catch (error) {
      if (error?.response?.status === 429) {
        setSubmitError("בוצעו יותר מדי נסיונות כושלים, נסו שוב עוד דקה")
      } else {
        setSubmitError("הקוד אינו תקין, בבקשה לנסות שוב")
      }
    }
  }

  return (
    <>
      <StyledWrapper>
        <StyledLink>
          <NextLink
            href={
              prevPath || prevPathLink || type === "balance"
                ? "/balance"
                : type === "groupPurchases"
                ? "/groupPurchases"
                : "/preview"
            }
            ariaLabel="חזרה לעמוד קודם"
            onClick={(e) => {
              e.preventDefault()
              router.back()
            }}
          >
            <BackIcon />

            <Typography variant="body1">חזרה</Typography>
          </NextLink>
        </StyledLink>
        <StyledBox>
          <StyledTitle>
            {type === "purchase" || type === "groupPurchases"
              ? "הזדהות באמצעות קוד אימות - עושים אימות קטן, ומתקדמים"
              : "הזדהות באמצעות קוד אימות"}
          </StyledTitle>
          <StyledTextWrapper>
            <StyledText $fontWeight="500" $marginTop="0px">
              ברגעים אלו נשלח קוד ב-
              <StyledSMSText>SMS</StyledSMSText> לטלפון הנייד שלך{" "}
            </StyledText>
            <StyledText $fontWeight="100" $marginTop="0px">
              {" "}
              שמספרו {formatPhone(phoneNumber)}
            </StyledText>
          </StyledTextWrapper>
          <Typography variant="srOnly">
            עליך להזין את הקוד כפי שקיבלת בהודעת SMS
          </Typography>
          <Typography
            variant="body1"
            style={{
              fontWeight: 500,
              fontFamily: "NarkisBlock",
              marginTop: 40,
            }}
          >
            עליך להזין את הקוד כפי שקיבלת בהודעת{" "}
            <StyledSMSText>SMS</StyledSMSText>
          </Typography>
          <StyledCodeInput
            setSubmitCode={setSubmitCode}
            setDisabled={setDisabled}
            submitError={submitError}
            setSubmitError={setSubmitError}
            handleSubmit={handleSubmit}
            ariaLabel="עליך להזין את הקוד שנשלח אליך לטפלון הנייד"
          />
          {submitError && <StyledError>{submitError}</StyledError>}
          <Timer mins={mins} secs={secs} setTime={setTime} />
          <StyledActionsWrap>
            <StyledButtonLink
              component="button"
              onClick={getVerificationCodeAgain}
              $disabled={mins === 0 && secs === 0}
            >
              שלחו לי קוד חדש
            </StyledButtonLink>
            {type === "purchase" ? (
              <NextLink href={`${editLink}/?focus=senderMobilePhone`} passHref>
                <StyledButtonLink component="a" $disabled={true}>
                  עריכת מספר טלפון
                </StyledButtonLink>
              </NextLink>
            ) : (
              type === "groupPurchases" && (
                <NextLink href={`${editLink}/?focus=phoneNumber`} passHref>
                  <StyledButtonLink component="a" $disabled={true}>
                    עריכת מספר טלפון
                  </StyledButtonLink>
                </NextLink>
              )
            )}
          </StyledActionsWrap>
          <StyledButtonWrapper>
            <GenericButton
              btnType="filled"
              disabled={
                disabled || (mins === 0 && secs === 0) || submitError
                  ? true
                  : false
              }
              onClick={handleSubmit}
              width={328}
            >
              אישור
            </GenericButton>
          </StyledButtonWrapper>
          {hiddenText()}
        </StyledBox>
      </StyledWrapper>
      <ReCAPTCHA
        ref={reCaptchaRef}
        sitekey={RECAPTCHA_KEY}
        size="invisible"
        hl="iw"
        tabIndex={-1}
      />
    </>
  )
}

export default OtpPage
